// Unified feature-chart container.
//
// One component for every surface that shows a feature chart:
//   * variant="card"   — feature detail page; renders inside a white card.
//   * variant="inline" — project features list; parent provides the card.
//
// Resolution: per-feature override → value-type default → cycle[0]
// (via VALUE_TYPE_CATALOG), skipping any pref not in the value type's cycle. The cycler in the chrome cycles through that
// value type's full cycle.

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useChartPrefs } from './useChartPrefs';
import { CHART_BODIES, VALUE_TYPE_CATALOG, systemDefaultFor, chartTitleSuffix } from './registry';
import { getValueType, type FeatureConfigLike } from './types';
import CyclerButton, { type KebabMenuItem } from './CyclerButton';
import ChartCardChrome from './ChartCardChrome';

interface Props {
  featureId: number;
  featureName?: string;
  unit?: string;
  config?: FeatureConfigLike | null;
  variant: 'card' | 'inline';
  // When set, the inline title is rendered as a Link to this path.
  // The card variant ignores this — its title is plain.
  titleLinkTo?: string;
}

const FeatureChartCard: React.FC<Props> = ({
  featureId, featureName, unit, config, variant, titleLinkTo,
}) => {
  const prefs = useChartPrefs();
  const valueType = getValueType(config);
  const catalog = VALUE_TYPE_CATALOG[valueType];

  // Only a chart in this value type's cycle is ever shown: `resolve` skips a
  // stale pref (e.g. a per-feature `pie` kept from a feature's enum days) and
  // lands on the system default. The body lookup's fallback still guards a
  // cycle entry whose body was removed.
  const chartId = prefs.resolve(valueType, featureId, catalog.cycle);
  const Body = CHART_BODIES[chartId] ?? CHART_BODIES[systemDefaultFor(valueType)];

  // For map features the "unit" exposed to the chart (axis labels,
  // tooltip, title suffix) is the value half's unit — top-level
  // `feature.unit` is unused for maps. Falls back to the prop so an
  // older map feature that somehow has a top-level unit still renders.
  const effectiveUnit = valueType === 'map' || valueType === 'map_numeric' || valueType === 'map_ordered_numeric'
    ? (config?.params?.value?.params?.unit ?? unit)
    : unit;

  // Title suffix varies with chart kind so the user always sees the
  // chart's y-axis dimension — `(hrs/time)` for the line chart of sleep,
  // `(checkins/hrs)` for its histogram.
  const suffix = chartTitleSuffix(chartId, effectiveUnit);
  const titleText = suffix
    ? `${featureName || 'Feature'} (${suffix})`
    : (featureName || 'Feature');

  const titleNode = variant === 'inline' && titleLinkTo ? (
    <Link
      to={titleLinkTo}
      className="text-lg font-medium text-amber-800 hover:text-amber-900"
    >
      {titleText}
    </Link>
  ) : (
    <h2 className={variant === 'card' ? 'text-xl font-semibold pl-2 sm:pl-0' : 'text-lg font-medium text-gray-800'}>
      {titleText}
    </h2>
  );

  // Modal title is always plain — the inline link doesn't make sense in
  // the fullscreen header.
  const modalTitleNode = (
    <h2 className="text-xl font-semibold">{titleText}</h2>
  );

  // Body-driven kebab toggle. Owned here (above both the body and the
  // CyclerButton) so the menu item and the body's brush stay in sync.
  // Only meaningful for the line/area chart, and only in fullscreen —
  // mirrors the horizontal brush, which is also fullscreen-only.
  const [verticalBrushVisible, setVerticalBrushVisible] = useState(false);

  const controls = catalog.cycle.length > 1
    ? (fullscreen: boolean) => {
        const extra: KebabMenuItem[] = [];
        if (fullscreen && chartId === 'line') {
          extra.push({
            label: verticalBrushVisible ? 'Hide vertical zoom' : 'Show vertical zoom',
            onClick: () => setVerticalBrushVisible((v) => !v),
          });
        }
        return (
          <CyclerButton
            featureId={featureId}
            valueType={valueType}
            currentChartId={chartId}
            prefs={prefs}
            extraMenuItems={extra}
          />
        );
      }
    : undefined;

  return (
    <ChartCardChrome
      variant={variant}
      title={titleNode}
      modalTitle={modalTitleNode}
      controls={controls}
      renderBody={(fullscreen) => (
        <Body
          featureId={featureId}
          featureName={featureName}
          unit={effectiveUnit}
          config={config}
          fullscreen={fullscreen}
          verticalBrushVisible={fullscreen && verticalBrushVisible}
        />
      )}
    />
  );
};

export default FeatureChartCard;
